import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Unauthorized: React.FC = () => {
  const [showError, setShowError] = useState(false);
  const location = useLocation();
  
  // Determine if this is likely a redirection in progress
  // Check if we came from auth or if we're being redirected elsewhere
  const isParentPortal = location.state?.from?.includes('parent-portal') || 
                        document.referrer.includes('parent-portal');
                        
  const isLikelyRedirecting = 
    location.state?.from?.includes('auth') || 
    location.state?.from?.includes('dashboard') ||
    document.referrer.includes('auth');

  // Customize message based on context
  const loadingMessage = isParentPortal 
    ? "Setting up your parent portal" 
    : "Setting up your portal";

  useEffect(() => {
    // Only show the error message after a delay
    // This gives time for any redirects to complete first
    const timer = setTimeout(() => {
      setShowError(true);
    }, isLikelyRedirecting ? 5000 : 3000); // Longer delay if it looks like a sign-in redirect
    
    return () => clearTimeout(timer);
  }, [isLikelyRedirecting]);

  // Initial loading state
  if (!showError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <Helmet>
          <title>Loading... | Hivetivity</title>
        </Helmet>
        
        <img 
          src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public" 
          alt="Hivetivity Logo" 
          className="h-16 mb-8" 
        />
        
        <div className="flex flex-col items-center">
          <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
          <h2 className="text-xl font-medium text-gray-700 mb-2">{loadingMessage}</h2>
          <p className="text-gray-500 text-center max-w-sm">
            Please wait while we load the Hivetivity platform...
          </p>
        </div>
      </div>
    );
  }

  // Only show error state after delay
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center bg-white">
      <Helmet>
        <title>401 - Unauthorized | Hivetivity</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="You do not have permission to access this page on Hivetivity." />
        <link rel="canonical" href="https://hivebubble.com/unauthorized" />
      </Helmet>
      
      <img 
        src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public" 
        alt="Hivetivity Logo" 
        className="h-12 mb-6" 
      />
      
      <h1 className="text-7xl font-bold text-primary mb-2">401</h1>
      <h2 className="text-2xl font-bold text-gray-800 mb-3">Unauthorized Access</h2>
      
      {isParentPortal && (
        <Alert variant="default" className="max-w-md mb-4 bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-700">
            It looks like your parent account may need additional setup. 
            Please contact your studio administrator for assistance.
          </AlertDescription>
        </Alert>
      )}
      
      <p className="text-gray-600 max-w-md mb-1">
        You don't have permission to access this page.
      </p>
      <p className="text-gray-600 max-w-md mb-6">
        Please contact your administrator or return to the dashboard.
      </p>
      
      <div className="flex flex-wrap gap-4 justify-center">
        <Button 
          className="bg-primary hover:bg-primary/90"
          asChild
        >
          <Link to="/dashboard">Go to Dashboard</Link>
        </Button>
        <Button 
          variant="outline"
          asChild
        >
          <Link to="/auth">Return to Sign In</Link>
        </Button>
        {isParentPortal && (
          <Button 
            variant="ghost"
            asChild
          >
            <Link to="/parent-portal">Try Parent Portal</Link>
          </Button>
        )}
      </div>
    </div>
  );
};

export default Unauthorized;
